/**
 * Studio icons: the figure picker draws each figure from its own geometry (so the icon IS the
 * shape), and the drawing tools get small flat-colour illustrations instead of glyphs.
 */
import React from 'react';
import { SHAPES_2D, SHAPES_3D, tones3D } from '../lib/shapeLibrary';

const ICON_BLUE = '#4f8ef7';

/** Icon for any figure key: the classic five, a library 2D figure, or a 3D object. */
export function ShapeIcon({ type, size = 20, color = 'currentColor' }) {
  const s = 18, o = (24 - s) / 2;
  const stroke = { fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinejoin: 'round', strokeLinecap: 'round' };
  const filled = { ...stroke, fill: color, fillOpacity: 0.18 };
  let body = null;
  if (type === 'line')            body = <line x1={o} y1={24 - o} x2={24 - o} y2={o} {...stroke} />;
  else if (type === 'arrow')      body = <g {...stroke}><line x1={o} y1={24 - o} x2={24 - o} y2={o} /><polyline points={`${24 - o - 6},${o} ${24 - o},${o} ${24 - o},${o + 6}`} /></g>;
  else if (type === 'circle')     body = <ellipse cx={12} cy={12} rx={s / 2} ry={s / 2} {...filled} />;
  else if (type === 'triangle')   body = <polygon points={`12,${o} ${o},${24 - o} ${24 - o},${24 - o}`} {...filled} />;
  else if (type === 'rectangle')  body = <rect x={o} y={o + 2} width={s} height={s - 4} {...filled} />;
  else if (SHAPES_2D[type]) {
    const def = SHAPES_2D[type];
    body = <path d={def.path(s, s)} transform={`translate(${o},${o})`} {...(def.open ? stroke : filled)} />;
  } else if (SHAPES_3D[type]) {
    const t = tones3D(ICON_BLUE);
    const id = `si-${type}`;
    body = (
      <g transform={`translate(${o},${o})`}>
        <defs>
          <radialGradient id={`${id}-r`} cx="35%" cy="30%" r="75%"><stop offset="0" stopColor={t.light} /><stop offset="0.55" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></radialGradient>
          <linearGradient id={`${id}-h`} x1="0" x2="1" y1="0" y2="0"><stop offset="0" stopColor={t.light} /><stop offset="0.45" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></linearGradient>
          <linearGradient id={`${id}-v`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={t.light} /><stop offset="0.45" stopColor={t.base} /><stop offset="1" stopColor={t.darker} /></linearGradient>
        </defs>
        {SHAPES_3D[type].parts(s, s).map((p, i) => (
          <path key={i} d={p.d} fillRule={p.evenOdd ? 'evenodd' : undefined}
            fill={p.fill === 'radial' ? `url(#${id}-r)` : p.fill === 'hgrad' ? `url(#${id}-h)` : p.fill === 'vgrad' ? `url(#${id}-v)` : (t[p.fill] || t.base)}
            stroke={t.darker} strokeWidth={0.5} strokeLinejoin="round" />
        ))}
      </g>
    );
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>{body}</svg>;
}

/** The "3D" mode toggle. */
export function Cube3DIcon({ size = 20, active = false }) {
  const t = tones3D(active ? '#ff6b6b' : ICON_BLUE);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <polygon points="3,8 12,3 21,8 12,13" fill={t.light} stroke={t.darker} strokeWidth="0.6" strokeLinejoin="round" />
      <polygon points="3,8 12,13 12,22 3,17" fill={t.base} stroke={t.darker} strokeWidth="0.6" strokeLinejoin="round" />
      <polygon points="21,8 12,13 12,22 21,17" fill={t.dark} stroke={t.darker} strokeWidth="0.6" strokeLinejoin="round" />
    </svg>
  );
}

/* ------------------------------ drawing tools ------------------------------ */

const Svg = ({ size, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>{children}</svg>
);

export function PencilIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <g transform="rotate(45 12 12)">
        <rect x="9" y="1.5" width="6" height="2.5" fill="#f28b9b" />
        <rect x="9" y="4" width="6" height="2" fill="#b9c3cf" />
        <rect x="9" y="6" width="6" height="10" fill="#f6c945" />
        <rect x="11" y="6" width="2" height="10" fill="#e6b52e" />
        <polygon points="9,16 15,16 12,21.5" fill="#e0a96d" />
        <polygon points="11,19.7 13,19.7 12,21.5" fill="#3b3b3b" />
      </g>
    </Svg>
  );
}

export function PenIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <g transform="rotate(45 12 12)">
        <rect x="9" y="2" width="6" height="4" rx="1" fill="#1d3b7a" />
        <rect x="9" y="6" width="6" height="10" fill="#4f8ef7" />
        <rect x="11" y="6" width="2" height="10" fill="#7fb0ff" />
        <polygon points="9,16 15,16 12.6,20.5 11.4,20.5" fill="#1d3b7a" />
      </g>
    </Svg>
  );
}

export function SprayIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <rect x="6" y="9" width="9" height="13" rx="1.5" fill="#f28c28" />
      <rect x="8" y="9" width="2.5" height="13" fill="#ffb066" />
      <rect x="7.5" y="6" width="6" height="3" fill="#b9c3cf" />
      <rect x="9" y="3.5" width="3" height="2.5" fill="#6b7785" />
      <circle cx="16.5" cy="5" r="1" fill="#7fb0ff" /><circle cx="19" cy="7.5" r="1.1" fill="#7fb0ff" />
      <circle cx="17" cy="10" r="0.9" fill="#7fb0ff" /><circle cx="20" cy="3.5" r="0.9" fill="#7fb0ff" />
    </Svg>
  );
}

export function EraserIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <g transform="rotate(-40 12 12)">
        <rect x="4" y="8" width="16" height="8" rx="1.5" fill="#6fa8ff" />
        <rect x="4" y="8" width="7" height="8" rx="1.5" fill="#f08aa0" />
        <rect x="4" y="13.5" width="16" height="2.5" fill="rgba(0,0,0,0.15)" />
      </g>
    </Svg>
  );
}

export function FillIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <g transform="rotate(-25 11 12)">
        <path d="M5 9 L17 9 L15.5 20 L6.5 20 Z" fill="#6fa8ff" />
        <path d="M5 9 L17 9 L16.5 12 L5.5 12 Z" fill="#3f6fd1" />
        <ellipse cx="11" cy="9" rx="6" ry="1.8" fill="#9cc2ff" />
      </g>
      <path d="M17.5 8 C17.5 8 21.5 13 21.5 15.5 A2 2 0 0 1 17.5 15.5 C17.5 13 17.5 8 17.5 8 Z" fill="#f6c945" />
      <path d="M6 4 C8 2.5 12 2.5 14 4 L14 6.5 C12 5 8 5 6 6.5 Z" fill="#f6c945" />
    </Svg>
  );
}

export function EyedropperIcon({ size = 20 }) {
  return (
    <Svg size={size}>
      <g transform="rotate(45 12 12)">
        <rect x="10" y="2" width="4" height="4" rx="2" fill="#f28b9b" />
        <rect x="9.5" y="6" width="5" height="2" fill="#6b7785" />
        <rect x="10.5" y="8" width="3" height="9" fill="#9aa5b1" />
        <polygon points="10.5,17 13.5,17 12,21" fill="#4f8ef7" />
      </g>
    </Svg>
  );
}

export const DRAW_TOOL_ICONS = {
  pen: PenIcon, pencil: PencilIcon, spray: SprayIcon, eraser: EraserIcon, fill: FillIcon, eyedropper: EyedropperIcon,
};
export function DrawToolIcon({ mode, size = 20 }) {
  const C = DRAW_TOOL_ICONS[mode] || PenIcon;
  return <C size={size} />;
}
